import React from "react";
import { useHistory } from "react-router-dom";
import { Clock, Home, User } from "lucide-react";
import PropertyInlineSummary from "../components/PropertyInlineSummary";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";
import { buildPropertyListingView } from "../utils/rentedPropertyDisplay";

interface IOffer {
  date: string;
  tenant: string;
  address: string;
  status: string;
  tenant_id: number;
  property_id: number;
}

interface ProposalCardProps {
  offer: IOffer;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}

function getProposalDays(proposalDate: string) {
  const [day, month, year] = proposalDate.split("/");
  const dateInMs = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10)
  ).getTime();
  const daysPassedInMs = Date.now() - dateInMs;
  return Math.floor(daysPassedInMs / (24 * 60 * 60 * 1000));
}

function getStatusMeta(status: string) {
  switch (status) {
    case "ACCEPT":
      return { label: "Aceito", className: "border-emerald-500/40 bg-emerald-500/15 text-emerald-400" };
    case "CANCELED":
      return { label: "Cancelado", className: "border-red-500/40 bg-red-500/15 text-red-400" };
    case "CONTESTED":
      return { label: "Pendente", className: "border-amber-500/40 bg-amber-500/15 text-amber-400" };
    default:
      return { label: "Pendente", className: "border-amber-500/40 bg-amber-500/15 text-amber-400" };
  }
}

export default function ProposalCard({
  offer,
  rentedPropertyCatalog,
}: ProposalCardProps) {
  const history = useHistory();
  const daysPassed = getProposalDays(offer.date);
  const status = getStatusMeta(offer.status);
  const detail = rentedPropertyCatalog?.findByPropertyId(offer.property_id);
  const listing = buildPropertyListingView(detail, {
    address: offer.address,
  });

  return (
    <div className="flex w-full items-start justify-between gap-3 rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4 transition-colors hover:border-[#6ea8ff]/25">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-2 text-xs text-[#8b949e]">
          <Clock className="h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" />
          <span>{daysPassed} dias atrás</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" />
          <p className="truncate text-sm font-semibold text-[#f0f6fc]">{offer.tenant}</p>
        </div>
        <div className="flex items-start gap-2">
          <Home className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#6ea8ff]" />
          <PropertyInlineSummary listing={listing} className="line-clamp-2" />
        </div>
      </div>
      <button
        type="button"
        onClick={() =>
          history.push(`/proposal-received/${offer.property_id}`, {
            client_id: offer.tenant_id,
          })
        }
        className={`shrink-0 rounded-full border px-3 py-1.5 text-[0.65rem] font-semibold transition-colors hover:opacity-90 ${status.className}`}
      >
        {status.label}
      </button>
    </div>
  );
}
